import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatQuantity } from '../../../../utils/formatters';

// ── Tipi condivisi (usati anche da SchedaOrdine e SchedaDistinta) ────────────

export interface CandidatoSorgente {
  origine: 'COMPONENTE' | 'RIGA';
  rigaOrdineId: number;
  rigaOrdinePos: number;
  rigaOrdineDescrizione: string;
  componenteId: number | null;
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  tipologia: string | null;
  unitaMisura: string | null;
  quantita: number | null;
  sfrido: number | null;
  giaEstratta: number;
  residuo: number | null;
}

export interface RigaEstratta {
  rigaOrdineId: number | null;
  componenteId: number | null;
  articoloId: number | null;
  descrizione: string;
  quantita: number;
  unitaMisura: string | null;
  note?: string | null;
}

interface RigaLibera {
  descrizione: string;
  quantita: string;
  unitaMisura: string;
}

interface Props {
  ordineId: number;
  tipo: 'MERCE' | 'LAVORAZIONI';
  titolo?: string;
  onClose: () => void;
  onConfirm: (righe: RigaEstratta[]) => void | Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// ModalEstraiRighe — estrazione righe distinta dai componenti/righe ordine.
// Mostra i candidati (GET /api/distinte/sorgente) con quantità già estratte
// e residuo; permette anche righe libere (materiale extra non a ordine).
// ─────────────────────────────────────────────────────────────────────────────
export default function ModalEstraiRighe({ ordineId, tipo, titolo, onClose, onConfirm }: Props) {
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [candidati, setCandidati] = useState<CandidatoSorgente[]>([]);
  const [selezionati, setSelezionati] = useState<Map<string, string>>(new Map()); // chiave → quantità (string input)
  const [righeLibere, setRigheLibere] = useState<RigaLibera[]>([]);

  const chiave = (c: CandidatoSorgente) =>
    c.componenteId ? `C-${c.componenteId}` : `R-${c.rigaOrdineId}`;

  useEffect(() => {
    let attivo = true;
    (async () => {
      setLoading(true);
      try {
        // @api GET /api/distinte/sorgente?ordineId&tipo -> {"success":true,"data":{"ordine":{"id":"number","numero":"string"},"candidati":[{"origine":"COMPONENTE|RIGA","rigaOrdineId":"number","componenteId":"number|null","articoloId":"number|null","codice":"string|null","descrizione":"string","unitaMisura":"string|null","quantita":"number|null","giaEstratta":"number","residuo":"number|null"}]}}
        const res = await apiFetch<{ success: boolean; data?: { candidati: CandidatoSorgente[] }; error?: string }>(
          `/api/distinte/sorgente?ordineId=${ordineId}&tipo=${tipo}`
        );
        if (!attivo) return;
        if (!res.success) {
          showToast(res.error || 'Errore nel caricamento delle righe estraibili', 'error');
          setCandidati([]);
        } else {
          setCandidati(res.data?.candidati ?? []);
        }
      } catch (e) {
        if (attivo) {
          showToast(e instanceof Error ? e.message : 'Errore nel caricamento', 'error');
          setCandidati([]);
        }
      } finally {
        if (attivo) setLoading(false);
      }
    })();
    return () => { attivo = false; };
  }, [ordineId, tipo]);

  const toggle = (c: CandidatoSorgente) => {
    setSelezionati((prev) => {
      const next = new Map(prev);
      const k = chiave(c);
      if (next.has(k)) {
        next.delete(k);
      } else {
        const def = c.residuo != null && c.residuo > 0 ? c.residuo : (c.quantita ?? 1);
        next.set(k, String(def));
      }
      return next;
    });
  };

  const setQuantita = (c: CandidatoSorgente, val: string) => {
    setSelezionati((prev) => {
      const next = new Map(prev);
      next.set(chiave(c), val);
      return next;
    });
  };

  const aggiungiRigaLibera = () =>
    setRigheLibere((prev) => [...prev, { descrizione: '', quantita: '', unitaMisura: '' }]);

  const setRigaLibera = (i: number, campo: keyof RigaLibera, val: string) =>
    setRigheLibere((prev) => prev.map((r, idx) => (idx === i ? { ...r, [campo]: val } : r)));

  const rimuoviRigaLibera = (i: number) =>
    setRigheLibere((prev) => prev.filter((_, idx) => idx !== i));

  const righeSelezionate = (): RigaEstratta[] | null => {
    const righe: RigaEstratta[] = [];
    for (const c of candidati) {
      const k = chiave(c);
      if (!selezionati.has(k)) continue;
      const q = Number(String(selezionati.get(k)).replace(',', '.'));
      if (!Number.isFinite(q) || q <= 0) {
        showToast(`Quantità non valida per "${c.descrizione}"`, 'error');
        return null;
      }
      righe.push({
        rigaOrdineId: c.rigaOrdineId,
        componenteId: c.componenteId,
        articoloId: c.articoloId,
        descrizione: c.descrizione,
        quantita: q,
        unitaMisura: c.unitaMisura,
      });
    }
    for (const rl of righeLibere) {
      if (!rl.descrizione.trim()) continue;
      const q = Number(rl.quantita.replace(',', '.'));
      if (!Number.isFinite(q) || q <= 0) {
        showToast(`Quantità non valida per la riga libera "${rl.descrizione}"`, 'error');
        return null;
      }
      righe.push({
        rigaOrdineId: null,
        componenteId: null,
        articoloId: null,
        descrizione: rl.descrizione.trim(),
        quantita: q,
        unitaMisura: rl.unitaMisura.trim() || null,
      });
    }
    return righe;
  };

  const conferma = async () => {
    const righe = righeSelezionate();
    if (!righe) return;
    if (righe.length === 0) {
      showToast('Seleziona almeno una riga (o compila una riga libera)', 'error');
      return;
    }
    setSalvando(true);
    try {
      await onConfirm(righe);
    } finally {
      setSalvando(false);
    }
  };

  const etichettaTipo = tipo === 'MERCE' ? 'merce' : 'lavorazioni';

  return (
    <div className="bop-modal-overlay" onClick={onClose}>
      <div className="bop-modal bop-modal--xl" onClick={(e) => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">{titolo ?? `Estrai righe ${etichettaTipo} dall'ordine`}</h2>
          <button type="button" className="bop-modal__close" onClick={onClose} aria-label="Chiudi">×</button>
        </div>
        <div className="bop-modal__body">
          {loading ? (
            <div className="bop-empty-state">Caricamento righe estraibili…</div>
          ) : (
            <>
              {candidati.length === 0 ? (
                <div className="bop-empty-state">
                  <p className="bop-empty-state__text">
                    Nessuna riga {etichettaTipo} estraibile da questo ordine.
                    Puoi comunque aggiungere righe libere.
                  </p>
                </div>
              ) : (
                <table className="bop-table">
                  <thead>
                    <tr>
                      <th className="bop-table__th--select"></th>
                      <th>Riga ordine</th>
                      <th>Codice</th>
                      <th>Descrizione</th>
                      <th>U.M.</th>
                      <th className="bop-text-right">Q.tà ordine</th>
                      <th className="bop-text-right">Già estratta</th>
                      <th className="bop-text-right">Residuo</th>
                      <th className="bop-text-right">Q.tà da estrarre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidati.map((c) => {
                      const k = chiave(c);
                      const sel = selezionati.has(k);
                      return (
                        <tr key={k}>
                          <td className="bop-table__td--select">
                            <input type="checkbox" checked={sel} onChange={() => toggle(c)} />
                          </td>
                          <td>#{c.rigaOrdinePos}</td>
                          <td>{c.codice ?? <span className="bop-text-muted">—</span>}</td>
                          <td>{c.descrizione}</td>
                          <td>{c.unitaMisura ?? '—'}</td>
                          <td className="bop-text-right">{c.quantita != null ? formatQuantity(c.quantita) : '—'}</td>
                          <td className="bop-text-right">{formatQuantity(c.giaEstratta)}</td>
                          <td className="bop-text-right">
                            {c.residuo != null
                              ? (c.residuo > 0 ? formatQuantity(c.residuo) : <span className="bop-badge bop-badge-success">Completa</span>)
                              : '—'}
                          </td>
                          <td className="bop-text-right">
                            <input
                              type="text"
                              className="bop-input bop-estrai__qta-input"
                              value={sel ? selezionati.get(k) ?? '' : ''}
                              disabled={!sel}
                              onChange={(e) => setQuantita(c, e.target.value)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Righe libere (materiale/lavorazione extra non a ordine) */}
              <div className="bop-estrai__libere">
                <div className="bop-flex-between">
                  <span className="bop-text-muted">Righe libere (extra non a ordine)</span>
                  <button type="button" className="bop-btn bop-btn-secondary bop-btn-sm" onClick={aggiungiRigaLibera}>
                    + Riga libera
                  </button>
                </div>
                {righeLibere.length > 0 && (
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Descrizione</th>
                        <th>Q.tà</th>
                        <th>U.M.</th>
                        <th className="bop-table__th--actions"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {righeLibere.map((r, i) => (
                        <tr key={i}>
                          <td>
                            <input className="bop-input" value={r.descrizione}
                              onChange={(e) => setRigaLibera(i, 'descrizione', e.target.value)} placeholder="Descrizione" />
                          </td>
                          <td>
                            <input className="bop-input bop-estrai__qta-input" value={r.quantita}
                              onChange={(e) => setRigaLibera(i, 'quantita', e.target.value)} placeholder="0,00" />
                          </td>
                          <td>
                            <input className="bop-input bop-estrai__um-input" value={r.unitaMisura}
                              onChange={(e) => setRigaLibera(i, 'unitaMisura', e.target.value)} placeholder="pz" />
                          </td>
                          <td className="bop-table__td--actions">
                            <button type="button" className="bop-btn bop-btn-danger bop-btn-sm"
                              onClick={() => rimuoviRigaLibera(i)}>Rimuovi</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
        <div className="bop-modal__footer">
          <button type="button" className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          <button type="button" className="bop-btn bop-btn-primary" disabled={loading || salvando} onClick={conferma}>
            {salvando ? 'Salvataggio…' : 'Aggiungi righe selezionate'}
          </button>
        </div>
      </div>
    </div>
  );
}
